/**
 * «Налилось на Polymarket»: шары пришли на кошелёк — видно в блокчейне сразу.
 *
 * Data API отстаёт на 170–190 с и без CORS, а время блока совпадает со временем
 * сделки. Поэтому смотрим события `TransferSingle` контракта Conditional Tokens
 * на кошелёк человека: `id` — токен исхода, `value / 10^6` — шары. Адрес
 * кошелька живёт только в браузере и на наш сервер не уходит.
 */

import { Decimal } from 'decimal.js';
import { useEffect, useState } from 'react';

import type { RowLeg } from '../lib/row';
import { tokensOf } from './polyBook';

const RPC = 'https://polygon.drpc.org';
const CTF = '0x4d97dcd97ec945f40cf65f87097ace5ea0476045';
const TRANSFER_SINGLE = '0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62';
const BACK_BLOCKS = 20;
export const FILL_POLL_MS = 3000;
export const FILL_EMPTY_MS = 20_000;
export const FILL_WATCH_MS = 90_000;

export interface ChainLog {
  address: string;
  topics: string[];
  data: string;
}

export const isWallet = (form: string): boolean => /^0x[0-9a-fA-F]{40}$/.test(form.trim());

/** Кошелёк как `topics[3]`: 32 байта, адрес дополнен нулями слева. */
export const walletTopic = (wallet: string): string =>
  `0x${'0'.repeat(24)}${wallet.trim().slice(2).toLowerCase()}`;

/** Сколько шаров токена `token` пришло на кошелёк в этих событиях; `null` — ни одного. */
export function sharesIn(logs: readonly ChainLog[], token: string, wallet: string): Decimal | null {
  const to = walletTopic(wallet);
  const want = BigInt(token);
  let total: Decimal | null = null;
  for (const log of logs) {
    if (log.address.toLowerCase() !== CTF) continue;
    if (log.topics[0]?.toLowerCase() !== TRANSFER_SINGLE) continue;
    if (log.topics[3]?.toLowerCase() !== to) continue;
    const body = log.data.startsWith('0x') ? log.data.slice(2) : log.data;
    if (body.length < 128) continue;
    if (BigInt(`0x${body.slice(0, 64)}`) !== want) continue;
    const value = new Decimal(BigInt(`0x${body.slice(64, 128)}`).toString()).div(1_000_000);
    total = (total ?? new Decimal(0)).plus(value);
  }
  return total;
}

async function rpc<T>(method: string, params: unknown[]): Promise<T | null> {
  try {
    const res = await fetch(RPC, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { result?: T };
    return body.result ?? null;
  } catch {
    return null;
  }
}

export async function startBlock(): Promise<string | null> {
  const head = await rpc<string>('eth_blockNumber', []);
  if (head === null) return null;
  return `0x${Math.max(0, Number.parseInt(head, 16) - BACK_BLOCKS).toString(16)}`;
}

export async function transfersTo(wallet: string, fromBlock: string): Promise<ChainLog[] | null> {
  return rpc<ChainLog[]>('eth_getLogs', [
    {
      address: CTF,
      fromBlock,
      toBlock: 'latest',
      topics: [TRANSFER_SINGLE, null, null, walletTopic(wallet)],
    },
  ]);
}

export type Fill =
  | { state: 'off' }
  | { state: 'wait' }
  | { state: 'got'; shares: string }
  | { state: 'none' };

/**
 * Пока карточка открыта: с момента `since` (нога Polymarket открыта или
 * отмечена) каждые 3 с до 90 с ищем шары токена ноги на кошельке. Через 20 с
 * без события — «шаров нет».
 */
export function useFill(
  wallet: string,
  leg: Pick<RowLeg, 'url' | 'asset'> | null,
  since: number | null,
): Fill {
  const [fill, setFill] = useState<Fill>({ state: 'off' });
  const url = leg?.url ?? null;
  const asset = leg?.asset ?? null;
  useEffect(() => {
    if (!isWallet(wallet) || (url === null && asset === null) || since === null) {
      setFill({ state: 'off' });
      return;
    }
    if (Date.now() - since > FILL_WATCH_MS) {
      setFill({ state: 'off' });
      return;
    }
    setFill({ state: 'wait' });
    let gone = false;
    let token: string | null = null;
    let from: string | null = null;
    const stop = (): void => {
      gone = true;
      clearInterval(timer);
    };
    const tick = async (): Promise<void> => {
      if (token === null) token = (await tokensOf({ url, asset }))?.mine ?? null;
      if (from === null) from = await startBlock();
      if (gone || token === null || from === null) return;
      const logs = await transfersTo(wallet, from);
      if (gone) return;
      const got = logs === null ? null : sharesIn(logs, token, wallet);
      if (got !== null) {
        setFill({ state: 'got', shares: got.toString() });
        stop();
        return;
      }
      const passed = Date.now() - since;
      if (passed >= FILL_EMPTY_MS) setFill({ state: 'none' });
      if (passed >= FILL_WATCH_MS) stop();
    };
    const timer = setInterval(() => void tick(), FILL_POLL_MS);
    void tick();
    return stop;
  }, [wallet, url, asset, since]);
  return fill;
}
